// client/src/hooks/useRTLLanguage.js
// Enhanced RTL Language Provider Hook for consistent language handling

import { useState, useEffect, useCallback } from 'react';
import { getTranslation, isRTL, initializeRTL } from '../translations/translations';

/**
 * Enhanced hook for managing RTL language state in public profile components
 * Ensures proper RTL layout even after page refresh
 */
export const useRTLLanguage = (propCurrentLanguage, propT) => {
  // Enhanced language state management with localStorage fallback
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    console.log('🔧 useRTLLanguage initializing with:', { propCurrentLanguage });
    
    // Priority: prop > localStorage > default
    if (propCurrentLanguage) return propCurrentLanguage;
    
    const savedLanguage = localStorage.getItem('preferred-language');
    console.log('💾 Saved language from localStorage:', savedLanguage);
    
    return savedLanguage || 'en';
  });

  // Enhanced translation function with fallback
  const t = useCallback((key, fallback = null) => {
    // Use prop function if available (from parent component)
    if (propT && typeof propT === 'function') {
      return propT(key, fallback);
    }
    
    // Otherwise use our own translation function
    return getTranslation(key, currentLanguage, fallback);
  }, [propT, currentLanguage]);

  // Critical: Apply RTL layout on component mount and language change
  useEffect(() => {
    console.log('🌐 useRTLLanguage applying RTL for language:', currentLanguage);
    
    const isRtl = isRTL(currentLanguage);
    const htmlElement = document.documentElement;
    
    // Set direction and language attributes
    htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    htmlElement.setAttribute('lang', currentLanguage);
    
    // Apply or remove RTL class
    if (isRtl) {
      htmlElement.classList.add('rtl-layout');
    } else {
      htmlElement.classList.remove('rtl-layout');
    }
    
    // Save language preference
    localStorage.setItem('preferred-language', currentLanguage);
    
    console.log('🎯 RTL setup completed:', {
      language: currentLanguage,
      isRtl,
      direction: htmlElement.getAttribute('dir'),
      hasRtlClass: htmlElement.classList.contains('rtl-layout'),
      htmlClasses: htmlElement.className
    });
    
    // Force a small delay to ensure styles are applied
    setTimeout(() => {
      // Double-check RTL application
      const finalDirection = htmlElement.getAttribute('dir');
      const finalRtlClass = htmlElement.classList.contains('rtl-layout');
      
      console.log('✅ RTL final check:', {
        language: currentLanguage,
        finalDirection,
        finalRtlClass,
        expected: isRtl ? 'rtl' : 'ltr'
      });
      
      // If there's a mismatch, force correct it
      if ((isRtl && finalDirection !== 'rtl') || (!isRtl && finalDirection !== 'ltr')) {
        console.warn('⚠️ RTL mismatch detected, forcing correction');
        htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
        
        if (isRtl) {
          htmlElement.classList.add('rtl-layout');
        } else {
          htmlElement.classList.remove('rtl-layout');
        }
      }
    }, 100);
    
  }, [currentLanguage]);

  // Update language when prop changes
  useEffect(() => {
    if (propCurrentLanguage && propCurrentLanguage !== currentLanguage) {
      console.log('🔄 Language prop changed:', { 
        from: currentLanguage, 
        to: propCurrentLanguage 
      });
      setCurrentLanguage(propCurrentLanguage);
    }
  }, [propCurrentLanguage, currentLanguage]);

  // Handle language change
  const handleLanguageChange = useCallback((newLanguage) => {
    console.log('🌍 Language change requested:', { 
      from: currentLanguage, 
      to: newLanguage 
    });
    
    setCurrentLanguage(newLanguage);
    
    // Immediately apply RTL for faster visual feedback
    const isRtl = isRTL(newLanguage);
    const htmlElement = document.documentElement;
    
    htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    htmlElement.setAttribute('lang', newLanguage);
    
    if (isRtl) {
      htmlElement.classList.add('rtl-layout');
    } else {
      htmlElement.classList.remove('rtl-layout');
    }
    
    localStorage.setItem('preferred-language', newLanguage);
  }, [currentLanguage]);

  // Return enhanced language state and utilities
  return {
    currentLanguage,
    setCurrentLanguage: handleLanguageChange,
    t,
    isRTL: isRTL(currentLanguage),
    // Utility functions
    initializeRTL: () => initializeRTL(currentLanguage),
    forceRTLCheck: () => {
      const htmlElement = document.documentElement;
      const expectedRTL = isRTL(currentLanguage);
      const currentDir = htmlElement.getAttribute('dir');
      const hasRtlClass = htmlElement.classList.contains('rtl-layout');
      
      console.log('🔍 Force RTL check:', {
        currentLanguage,
        expectedRTL,
        currentDir,
        hasRtlClass,
        needsCorrection: (expectedRTL && currentDir !== 'rtl') || (!expectedRTL && currentDir !== 'ltr')
      });
      
      // Force correction if needed
      if ((expectedRTL && currentDir !== 'rtl') || (!expectedRTL && currentDir !== 'ltr')) {
        console.log('🔧 Applying RTL correction');
        htmlElement.setAttribute('dir', expectedRTL ? 'rtl' : 'ltr');
        
        if (expectedRTL) {
          htmlElement.classList.add('rtl-layout');
        } else {
          htmlElement.classList.remove('rtl-layout');
        }
        
        return true; // Correction was applied
      }
      
      return false; // No correction needed
    }
  };
};

/**
 * Higher-order component for wrapping public profile components with RTL support
 */
export const withRTLSupport = (WrappedComponent) => {
  return function RTLSupportedComponent(props) {
    const rtlProps = useRTLLanguage(props.currentLanguage, props.t);
    
    return <WrappedComponent {...props} {...rtlProps} />;
  };
};

/**
 * Utility component for debugging RTL state
 */
export const RTLDebugger = () => {
  const [debugInfo, setDebugInfo] = useState({});
  
  useEffect(() => {
    const updateDebugInfo = () => {
      const htmlElement = document.documentElement;
      const savedLanguage = localStorage.getItem('preferred-language');
      
      setDebugInfo({
        timestamp: new Date().toISOString(),
        savedLanguage,
        htmlDir: htmlElement.getAttribute('dir'),
        htmlLang: htmlElement.getAttribute('lang'),
        hasRtlClass: htmlElement.classList.contains('rtl-layout'),
        htmlClasses: htmlElement.className,
        isRTLExpected: isRTL(savedLanguage || 'en')
      });
    };
    
    updateDebugInfo();
    
    // Update debug info every second
    const interval = setInterval(updateDebugInfo, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  if (process.env.NODE_ENV !== 'development') {
    return null; // Only show in development
  }
  
  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      right: '10px',
      background: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '10px',
      borderRadius: '5px',
      fontSize: '12px',
      fontFamily: 'monospace',
      zIndex: 9999,
      minWidth: '200px'
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>RTL Debug Info</div>
      <div>Language: {debugInfo.savedLanguage}</div>
      <div>HTML Dir: {debugInfo.htmlDir}</div>
      <div>HTML Lang: {debugInfo.htmlLang}</div>
      <div>RTL Class: {debugInfo.hasRtlClass ? '✅' : '❌'}</div>
      <div>Expected RTL: {debugInfo.isRTLExpected ? '✅' : '❌'}</div>
      <div style={{ 
        color: debugInfo.htmlDir === (debugInfo.isRTLExpected ? 'rtl' : 'ltr') ? '#90EE90' : '#FFB6C1',
        fontWeight: 'bold'
      }}>
        Status: {debugInfo.htmlDir === (debugInfo.isRTLExpected ? 'rtl' : 'ltr') ? 'OK' : 'MISMATCH'}
      </div>
      <div style={{ fontSize: '10px', marginTop: '5px', opacity: 0.7 }}>
        {debugInfo.timestamp?.split('T')[1]?.split('.')[0]}
      </div>
    </div>
  );
};

/**
 * Force RTL initialization utility - call this in main App component
 */
export const forceRTLInitialization = () => {
  console.log('🚀 Force RTL initialization called');
  
  const savedLanguage = localStorage.getItem('preferred-language');
  
  if (savedLanguage) {
    console.log('🔄 Applying saved language:', savedLanguage);
    
    const isRtl = isRTL(savedLanguage);
    const htmlElement = document.documentElement;
    
    htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    htmlElement.setAttribute('lang', savedLanguage);
    
    if (isRtl) {
      htmlElement.classList.add('rtl-layout');
    } else {
      htmlElement.classList.remove('rtl-layout');
    }
    
    console.log('✅ RTL force initialization completed:', {
      language: savedLanguage,
      isRtl,
      direction: htmlElement.getAttribute('dir'),
      hasRtlClass: htmlElement.classList.contains('rtl-layout')
    });
  } else {
    console.log('ℹ️ No saved language found, using default (en)');
  }
};

export default useRTLLanguage;